import React from 'react';
import { Descriptions, Typography } from 'antd';
// Types
import { TicketOrderKind } from 'api/graphql-global-types';
// Helpers
import {
  formatEventDateTime,
  formatMoney,
  getTicketSerials,
  OrderRow,
} from 'pages/Events/helpers';
// Components
import OrderHistory from 'pages/Events/components/OrderHistory/OrderHistory';

const { Text } = Typography;

const COLUMNS = 3;

type OrderDetailsProps = {
  order: OrderRow;
  timezone?: string | null;
  showPromoCode?: boolean;
};

/** The expanded row of an orders table. */
const OrderDetails = ({
  order,
  timezone,
  showPromoCode = false,
}: OrderDetailsProps): JSX.Element => {
  const charges =
    order.kind === TicketOrderKind.Comp
      ? []
      : [
          { label: 'Service fee', value: formatMoney(order.serviceFeeAmount) },
          { label: 'Discount', value: formatMoney(order.discountAmount) },
          ...(showPromoCode
            ? [{ label: 'Promo code', value: order.promocodeCode || '—' }]
            : []),
          { label: 'Presale code', value: order.presaleCode || '—' },
        ];
  // The order id fills what is left of its row after the charges and the confirmation
  const orderIdSpan = COLUMNS - ((charges.length + 1) % COLUMNS);

  return (
    <Descriptions size="small" column={{ xs: 1, md: COLUMNS }}>
      <Descriptions.Item label="Ticket numbers" span={COLUMNS}>
        {getTicketSerials(order).join(', ') || '—'}
      </Descriptions.Item>
      {charges.map(({ label, value }) => (
        <Descriptions.Item key={label} label={label}>
          {value}
        </Descriptions.Item>
      ))}
      <Descriptions.Item label="Confirmed">
        {formatEventDateTime(order.confirmedAt, timezone)}
      </Descriptions.Item>
      <Descriptions.Item label="Order ID" span={orderIdSpan}>
        <Text code copyable>
          {order.orderId}
        </Text>
      </Descriptions.Item>
      {order.refundFailure && (
        <Descriptions.Item label="Last refund failed" span={COLUMNS}>
          {order.refundFailure.reason}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="History" span={COLUMNS}>
        <OrderHistory
          orderId={order.orderId}
          kind={order.kind}
          timezone={timezone}
        />
      </Descriptions.Item>
    </Descriptions>
  );
};

export default OrderDetails;
